/* eslint-disable @typescript-eslint/no-explicit-any */
type StoryLink = {
  id?: string;
  cached_url?: string;
  anchor?: string;
  linktype?: "story";
  target?: "_self" | "_blank";
  story?: {
    name: string;
    created_at?: string;
    published_at?: string;
    id: number;
    uuid: string;
    content?: {
      [k: string]: unknown;
    };
    slug: string;
    full_slug: string;
    sort_by_date?: null | string;
    position?: number;
    tag_list?: string[];
    is_startpage?: boolean;
    parent_id?: null | number;
    meta_data?: null | {
      [k: string]: unknown;
    };
    group_id?: string;
    first_published_at?: string;
    release_id?: null | number;
    lang?: string;
    path?: null | string;
    alternates?: any[];
    default_full_slug?: null | string;
    translated_slugs?: null | any[];
    [k: string]: unknown;
  };
};

type URLLink = {
  url?: string;
  cached_url?: string;
  anchor?: string;
  linktype?: "asset" | "url";
  target?: "_self" | "_blank";
};

type EmailLink = {
  email?: string;
  linktype?: "email";
  target?: "_self" | "_blank";
};

export type MultilinkStoryblok = StoryLink | URLLink | EmailLink;

export type LinkReturn = {
  href: string;
  rawHref: string;
  email?: string;
  target?: "_self" | "_blank";
  anchor?: string | unknown;
  type: "story" | "url" | "email";
  external?: boolean;
} | null;

function getStoryLink(link: StoryLink): LinkReturn {
  let href: string | undefined;
  if ("cached_url" in link) {
    href = link.cached_url;
  }

  if ("url" in link && typeof link.url === "string" && !!link.url) {
    href = link.url;
  }

  if ("story" in link && link.story && "url" in link.story) {
    href = link.story.url as string | undefined;
  }

  if (
    "story" in link &&
    link.story &&
    "full_slug" in link.story &&
    !!link.story.full_slug
  ) {
    href = link.story.full_slug as string;
  }

  if (!href) return null;

  return {
    href: parseHref(href),
    rawHref: href,
    ...getAdditionalLinkParams(link),
    type: "story",
  };
}

export function getLink(
  link: string,
  removeSegments?: string[]
): NonNullable<LinkReturn>;
export function getLink(
  link: MultilinkStoryblok | undefined,
  removeSegments?: string[]
): LinkReturn;
export function getLink(
  link: MultilinkStoryblok | string | undefined,
  removeSegments?: string[]
): LinkReturn {
  function getLinkInternal() {
    if (!link) return null;

    if (typeof link === "string") {
      const href = parseHref(link);
      return {
        href: href,
        rawHref: link,
        type: "url",
        external: href.startsWith("http"),
      } as const;
    }

    // Escape empty links.

    if (
      "url" in link &&
      link.url === "" &&
      "id" in link &&
      link.id === "" &&
      // @ts-expect-error linktype is not always present
      (link.linktype === "url" || link.linktype === "story")
    ) {
      return null;
    }

    if (link.linktype === "story" || "story" in link) {
      const result = getStoryLink(link as StoryLink);

      return result;
    }

    if (link.linktype === "url") {
      const href = link.url || link.cached_url;

      if (!href) return null;

      const newLocal = parseHref(href);
      return {
        href: newLocal,
        rawHref: href,
        ...getAdditionalLinkParams(link),
        type: "url",
        external: newLocal.startsWith("http"),
      } as const;
    }

    if (link.linktype === "email") {
      const href = `mailto:${link.email}`;
      return {
        href,
        rawHref: href,
        email: link.email,
        ...getAdditionalLinkParams(link),
        type: "email",
      } as const;
    }

    return null;
  }

  const parsedLink = getLinkInternal();

  if (parsedLink) {
    if (removeSegments?.length) {
      // Remove paths can be "products", "pages" etc
      const split = parsedLink.href.split("/");
      removeSegments.forEach((path) => {
        const index = split.indexOf(path);
        if (index !== -1) {
          split.splice(index, 1);
        }
      });

      return {
        ...parsedLink,
        href: split.join("/"),
      };
    }

    return parsedLink;
  }

  return null;
}

// Helpers

function getAdditionalLinkParams(link: MultilinkStoryblok) {
  return {
    target: link.target,
    linkType: link.linktype,
  };
}

function parseHref(href: string): string {
  if (href.startsWith("/")) {
    return href;
  }

  if (href.startsWith("http")) {
    return href;
  }

  if (href.startsWith("#")) {
    return href;
  }

  return `/${href}`;
}
